// Guías de Aprender · Estrategia (2026-10-18), basadas en el libro «Cómo llegar a la Xima».

export const ESTRATEGIA_GUIDES = [
  {
    slug: "estrategia",
    theme: "estrategia",
    nav: "Estrategia: por dónde empezar",
    title: "Estrategia de XIMA: claves para ganar más partidas",
    description:
      "Las ideas estratégicas de XIMA en una sola guía: la lucha por el segundo piso, cuándo meter el especial, los centros, la iniciativa y cómo se gana un final.",
    h1: "Estrategia de XIMA",
    intro:
      "Una partida de XIMA tiene tres fases, como el ajedrez: apertura, medio juego y final. En cada una el objetivo inmediato es distinto, pero todas giran alrededor de lo mismo: subir antes que el rival.",
    sections: [
      {
        h: "La idea central: subir primero",
        p: [
          "El libro lo resume en una frase: tiene más posibilidades de llegar a la cima quien primero lleve al menos dos bloques, uno de ellos el especial, a la tercera planta. Y para eso antes ha tenido que llevar al menos tres bloques al segundo piso antes que el rival.",
          "Por eso «las mejores jugadas de ambos bandos están siempre subordinadas a la lucha por la segunda planta».",
        ],
      },
      {
        h: "Las tres fases",
        defs: [
          ["Apertura", "Meter bloques con rapidez (de 5 a 7), preparar escalones y llegar al segundo piso antes que el rival. Ver Aperturas y Defensas."],
          ["Medio juego", "Empieza cuando los dos han desarrollado sus bloques, normalmente cuando entra el especial. Es la fase más compleja: se lucha por los centros y se preparan las rutas hacia la tercera planta. Ver Medio juego, Maniobra Zil."],
          ["Final", "Quedan pocos bloques activos en la tercera planta y se decide quién llega a la cima. Ver Finales y Oposición."],
        ],
      },
      {
        h: "Diez principios para jugar mejor",
        list: [
          "Cada tiempo cuenta: en la apertura, dos tiempos valen a menudo más que un bloque.",
          "No salgas a cazar bloques en las primeras jugadas.",
          "Prepara siempre más de una vía de subida: una sola se bloquea con facilidad.",
          "No metas el especial pronto; hazlo cuando pueda entrar alto y seguro.",
          "Guarda un bloque común fuera para el final.",
          "Domina los escalones del rival para cortar su subida.",
          "Lucha por el centro medio (segundo piso) y el centro alto (tercer piso).",
          "Coordina tus bloques: piezas sueltas que no pueden apoyarse son una mala posición.",
          "Mantén la iniciativa: busca amenazas que obliguen al rival a responder.",
          "En el final, recuerda que dos bloques ganan a uno.",
        ],
      },
      {
        h: "Estudia primero los finales",
        p: [
          "El libro recomienda estudiar los finales antes que nada, porque en ellos se ve con claridad qué hace falta para llegar a la cima, y eso da sentido a todo lo que haces en la apertura y el medio juego. Los puzzles diarios son una buena forma de practicarlos.",
        ],
      },
    ],
    faqs: [
      ["¿Qué es lo más importante en XIMA?", "Llegar antes que el rival a cada piso, empezando por la lucha por el segundo."],
      ["¿Hay teoría de aperturas en XIMA?", "Sí. El libro describe 13 casillas de apertura distintas y da nombre a las principales, como Liz, Susy o Capablanca."],
    ],
    related: [
      "estrategia/aperturas-xima",
      "estrategia/medio-juego",
      "estrategia/finales-xima",
      "estrategia/maniobra-zil",
      "estrategia/maniobra-feedback",
    ],
    cta: { label: "Aperturas", slug: "estrategia/aperturas-xima" },
    legacy: [
      "articulos/como-mejorar-la-toma-de-decisiones",
      "articulos/tactica-vs-estrategia",
      "articulos/aprender-de-una-derrota",
      "articulos/planificar-varios-movimientos",
      "articulos/errores-comunes-juegos-estrategia",
      "articulos/juegos-estrategia-pensamiento-logico",
    ],
    en: "en/strategy",
  },
  {
    slug: "estrategia/aperturas-xima",
    theme: "estrategia",
    nav: "Aperturas",
    title: "Aperturas de XIMA: las primeras jugadas y sus nombres",
    description:
      "Las aperturas de XIMA: por qué solo hay 13 casillas de salida distintas y cómo son las aperturas Liz, Susy, Muiño, Tanu, Párragueña, Capablanca y Yosdel.",
    h1: "Aperturas",
    intro:
      "La forma en que empiezas una partida condiciona todo lo demás. El objetivo de toda buena apertura es poner tus bloques en juego para responder al rival y subir cuanto antes.",
    sections: [
      {
        h: "El objetivo de la apertura",
        p: [
          "Desarrollar los bloques con rapidez y eficacia. Hay que evitar las jugadas inútiles, mover una y otra vez el mismo bloque o atacar antes de tiempo con material insuficiente: todo eso pierde tiempos.",
          "En la apertura el primer jugador tiene la iniciativa, mientras que el segundo tiene cierta ventaja posicional, porque ve la primera jugada antes de decidir. Para el que sale, perder un tiempo puede ser muy grave.",
        ],
      },
      {
        h: "Solo 13 aperturas distintas",
        p: [
          "En el primer movimiento hay 176 jugadas posibles: 88 casillas del primer piso, y en cada una puedes entrar con un bloque común o con el especial. Pero como la pirámide tiene cuatro caras iguales y cada cara es simétrica, muchas de esas jugadas son la misma idea vista desde otro lado (B1, por ejemplo, equivale a A2).",
          "Al final solo hay 13 casillas de apertura realmente distintas, y 26 aperturas si contamos que se puede salir con el común o con el especial.",
        ],
      },
      {
        h: "Las aperturas con nombre",
        table: {
          head: ["Apertura", "Casillas", "Idea"],
          rows: [
            ["Yosdel", "A1, M1, M13, A13", "Escalera natural en B2 con una sola vía de subida, muy rápida pero fácil de bloquear. Busca controlar esquinas y atacar rápido por la diagonal."],
            ["Capablanca", "A2, B1, L1, M2, M12, L13, B13, A12", "Escalera natural en C3 con dos vías. Ni tan rápida como para bloquearla fácil ni tan lenta para subir al segundo piso. Quizá la más equilibrada."],
            ["Párragueña", "A3, C1, K1, M3, M11, K13, C13, A11", "Dos escaleras naturales juntas (C3 y D3) con cinco vías. Ofensiva y defensiva: controla el anillo interior por una banda y la esquina por la otra."],
            ["Tanu o ucraniana", "A4, D1, J1, M4, M10, J13, D13, A10", "Dos escaleras naturales y una artificial, con ocho vías. Muy útil para el final: defiende la fila interior del segundo piso."],
            ["Muiño", "A5, E1, I1, M5, M9, I13, E13, A9", "Tres escaleras naturales con nueve vías. Gran control del segundo piso; típica de jugadores agresivos que suben el especial cuanto antes."],
            ["Susy", "A6, F1, H1, M6, M8, H13, F13, A8", "Una de las más dinámicas: permite coordinar por columnas y filas vecinas y dominar la cara en la que se juega."],
            ["Liz", "A7, G1, M7, G13", "Apertura central, directa hacia la cima. Mucho control desde el inicio; típica de jugadores técnicos y precisos."],
          ],
        },
        after: [
          "Cuando la apertura empieza con el bloque especial se le añade la palabra Especial: «1. =A6» es la Apertura Especial Susy, y «1. -A6» la Apertura Susy.",
          "El libro aclara que no se puede decir a ciencia cierta que una apertura sea buena o mala, porque los planes posibles son infinitos.",
        ],
      },
      {
        h: "Cada apertura, en detalle",
        p: ["Cada apertura tiene su propia guía, con sus casillas en el tablero, cuánto se juega en juegoxima.com y una partida real que empieza así."],
        links: [
          "estrategia/apertura-capablanca",
          "estrategia/apertura-yosdel",
          "estrategia/apertura-parraguena",
          "estrategia/apertura-tanu",
          "estrategia/apertura-muino",
          "estrategia/apertura-susy",
          "estrategia/apertura-liz",
        ],
      },
      {
        h: "Principios de apertura",
        list: [
          "Desarrolla rápido tus bloques comunes; mete de 5 a 7 lo antes posible.",
          "No muevas una y otra vez el mismo bloque.",
          "Alinea y coordina tus bloques.",
          "Intenta subir al segundo piso antes que el rival.",
          "Controla las esquinas, ocupándolas o amenazándolas.",
          "No te dejes dominar ningún bloque y domina siempre que puedas.",
          "Dobla columna lo antes posible.",
          "Defiende el anillo interior y vigila las vías de subida del rival.",
          "No juegues un bloque que no pueda recibir apoyo por el anillo interior.",
          "Designa uno o dos bloques escalón, no más: los escalones están condenados a no subir.",
          "No salgas a cazar bloques en las primeras jugadas.",
          "Si metes el especial, que sea en el segundo o tercer piso y, si puedes, por el anillo interior de la misma banda que el especial contrario.",
        ],
      },
    ],
    faqs: [
      ["¿Cuántas aperturas tiene XIMA?", "13 casillas de apertura distintas, que dan 26 aperturas si se cuenta salir con el común o con el especial."],
      ["¿Cuál es la mejor apertura?", "No hay una mejor: depende del plan. Capablanca es quizá la más equilibrada y Liz la más directa hacia la cima."],
    ],
    related: ["estrategia/defensas-xima", "estrategia/maniobra-feedback", "reglas/subir-bloques", "historia-de-xima"],
    cta: { label: "Defensas", slug: "estrategia/defensas-xima" },
    en: "en/openings",
  },
  {
    slug: "estrategia/defensas-xima",
    theme: "estrategia",
    nav: "Defensas",
    title: "Defensas de XIMA: cómo responder a una apertura",
    description:
      "Las defensas de XIMA según el libro: bajas, medias, altas, centrales, de contraataque y gambitos. En qué casillas se juega cada una y para qué sirve.",
    h1: "Defensas",
    intro:
      "Las defensas son las respuestas que mejor previenen el ataque de una apertura. El segundo jugador tiene hasta 87 casillas posibles para su primera jugada, y el libro las clasifica por su objetivo.",
    sections: [
      {
        h: "Tipos de defensa",
        defs: [
          ["Bajas (o universales)", "A2, A12, B13, L13, M12, M2, L1 y B1. Plantan cara a la apertura en la primera línea de combate, en el mismo primer piso, para que el rival no consiga apoyo hacia el segundo."],
          ["Medias", "A3, A4, A10, A11, C13, D13, J13, K13, M11, M10, M4, M3, J1, K1, C1 y D1. No estorban directamente la apertura, pero preparan un contraataque en la subida al segundo piso mediante bloqueo u obstrucción, inutilizando con un solo bloque varios bloques rivales."],
          ["Altas o superiores", "A5, A6, A8, A9, E13, F13, H13, I13, M8, M9, M5, M6, H1, I1, E1 y F1. Contraatacan la vía de subida a la tercera planta. Son muy técnicas, para expertos: dan libertad al rival y solo funcionan con un contraataque eficaz."],
          ["Altas centrales", "G1, A7, G13 y M7, las centrales del anillo exterior. Amenazan una o varias casillas importantes o desvían con un sacrificio la ofensiva rival."],
          ["De contraataque o escaleras", "Las casillas interiores del primer piso (B3 a B11, C12 a K12, L3 a L11 y C2 a K2). Dejan que el rival llegue antes al segundo piso a cambio de que no pueda usar de momento su bloque de apertura."],
          ["Gambitos", "Se amenaza directamente al bloque de apertura desde una casilla exterior para tentar al rival a dominarlo y tomar así la iniciativa en otra parte del tablero."],
        ],
      },
      {
        h: "Directas e indirectas",
        p: [
          "Según la casilla de apertura, una defensa puede ser directa (su línea de ataque se cruza con la del bloque que salió en el segundo o tercer piso) o indirecta. Las defensas también pueden ser internas o externas, según el anillo en el que estén.",
        ],
      },
      {
        h: "La casilla que hay que evitar",
        p: [
          "Según el libro, todas las primeras respuestas se consideran jugables salvo, por ahora, las esquinas interiores B2, L2, L12 y B12.",
        ],
      },
      {
        h: "Una buena defensa",
        p: [
          "Una defensa fuerte no solo detiene una amenaza: también conserva tus propias vías de subida y evita que el rival gane el segundo piso con facilidad. Defender en XIMA es, a la vez, preparar tu ascenso.",
        ],
      },
    ],
    faqs: [
      ["¿Qué casillas no conviene jugar como primera respuesta?", "Las esquinas interiores B2, L2, L12 y B12."],
      ["¿Qué es un gambito en XIMA?", "Una defensa que ofrece un bloque al rival para que lo domine y así tomar la iniciativa en otra zona."],
    ],
    related: ["estrategia/aperturas-xima", "reglas/bloqueo-y-obstruccion", "estrategia/medio-juego"],
    cta: { label: "Medio juego", slug: "estrategia/medio-juego" },
  },
  {
    slug: "estrategia/medio-juego",
    theme: "estrategia",
    nav: "Medio juego",
    title: "Medio juego en XIMA: centros, espacio y armonía",
    description:
      "Cómo jugar el medio juego de XIMA: el centro medio y el centro alto, el espacio, la administración de bloques, la armonía, la iniciativa y los cambios de bloques.",
    h1: "Medio juego",
    intro:
      "El medio juego empieza cuando los dos jugadores han desarrollado sus bloques. No hay una teoría jugada a jugada como en la apertura, y por eso es la fase más compleja: aquí cuentan los principios.",
    sections: [
      {
        h: "Los dos centros",
        p: [
          "Como el tablero es tridimensional, XIMA tiene dos centros. El centro medio es todo el segundo piso y el centro alto, todo el tercero. A menudo un jugador controla uno y el otro intenta equilibrar con el otro: quien domina el segundo piso tiene más espacio, pero quien coloca un bloque bien situado en el tercero puede bloquear varios bloques rivales con un solo movimiento.",
          "Una vez elegido el centro que vas a dominar, haz un plan táctico para no perder la iniciativa.",
        ],
      },
      {
        h: "El espacio",
        p: [
          "El espacio es la cantidad de casillas que domina cada jugador, donde puede colocar sus bloques. Está muy ligado al segundo piso: para llegar al tercero hay que pasar por el segundo, así que quien gana la batalla del segundo piso gana espacio. Son los bloques comunes los que conquistan ese espacio y desalojan a los rivales para dejar sitio al especial.",
        ],
      },
      {
        h: "La administración de los bloques",
        list: [
          "En la apertura, no metas más de 5 a 7 bloques.",
          "En el medio juego, ten siempre al menos un bloque que proteja la entrada y la subida del especial.",
          "Mete el especial como sexto, séptimo u octavo bloque.",
          "Guarda tu último bloque común hasta el final o, al menos, mételo después del último bloque del rival. Cuando el rival ya no tenga bloques fuera, los escalones que quedaron sueltos te permitirán llegar con un solo movimiento a la tercera planta.",
        ],
      },
      {
        h: "La armonía",
        p: [
          "Armonía es coordinar las fuerzas en la ejecución de un plan para ocupar las casillas más ventajosas. Ojo: las fuerzas y su objetivo tienen que corresponderse. Nada se consigue coordinando bloques sobre un objetivo mal elegido. Unos bloques sueltos que no pueden coordinarse en un plazo razonable forman una mala posición.",
        ],
      },
      {
        h: "La iniciativa",
        p: [
          "Tener la iniciativa es marcar el ritmo: obligar al rival a responder a tus amenazas de subida. La amenaza es su arma principal, así que intenta convertir una amenaza simple en doble o múltiple; eso dificulta mucho la defensa. El ataque doble, atacar dos bloques o casillas a la vez con un solo bloque, es el tema táctico más típico, y solo es posible si hay algo débilmente defendido.",
        ],
      },
      {
        h: "Cambios y sacrificios",
        p: ["En XIMA los cambios de bloques deben estar siempre justificados. Pueden buscar:"],
        list: [
          "Imponer una ventaja de material.",
          "Ganar tiempos.",
          "Eliminar defensores importantes del rival.",
          "Eliminar o desviar los bloques con los que te ataca.",
          "Estropear la estructura de bloques del contrario.",
          "Pasar a un final que te favorece.",
        ],
        after: [
          "A veces se sacrifica material para ganar tiempo e iniciativa: los bloques valen por el trabajo que hacen, no por existir.",
        ],
      },
      {
        h: "Otros principios del medio juego",
        list: [
          "Si tu especial puede llegar libre a la tercera planta, llévalo a las centrales o a las diagonales del anillo exterior, alineado con G7.",
          "Pon el especial a salvo antes de una maniobra final.",
          "Si tienes ventaja de desarrollo, abre el juego; si no, ciérralo.",
          "Amenaza al especial contrario siempre que puedas y domínalo cuando sea posible.",
          "Usa los bloques más avanzados o en columna para crear estructuras de subida.",
          "Juega más bloques por los anillos exteriores, dejando solo uno por el interior en cada piso.",
          "No apiles más de tres bloques propios en una casilla, y menos en el anillo interior.",
          "Ten claro cómo vas a llegar a G7, aunque el rival no existiera.",
        ],
      },
    ],
    faqs: [
      ["¿Cuándo empieza el medio juego?", "Cuando los dos jugadores han desarrollado sus bloques; normalmente, al entrar el especial."],
      ["¿Qué es el centro en XIMA?", "Hay dos: el centro medio (segundo piso) y el centro alto (tercer piso)."],
    ],
    related: ["estrategia/maniobra-zil", "estrategia/valor-de-los-bloques", "reglas/amenazas-al-especial", "estrategia/finales-xima"],
    cta: { label: "Maniobra Zil", slug: "estrategia/maniobra-zil" },
    legacy: [
      "estrategia/espacio",
      "estrategia/administracion-bloques",
      "estrategia/principio-armonia",
      "estrategia/iniciativa",
      "estrategia/cambios-de-bloques",
      "estrategia/material-por-tiempo",
      "estrategia/bloques-escalones",
    ],
  },
  {
    slug: "estrategia/maniobra-zil",
    theme: "estrategia",
    nav: "Maniobra Zil",
    title: "La maniobra Zil de XIMA: llevar el especial al tercer piso",
    description:
      "La maniobra Zil del medio juego de XIMA en sus 4 fases: bloque en la tercera planta, semicoordinación con el segundo piso, escalera y entrada del especial.",
    h1: "Maniobra Zil",
    intro:
      "La maniobra Zil es la única maniobra del medio juego descrita en el libro. Su misión es llevar el bloque especial a la tercera planta, o dejarle el camino abierto, prácticamente sin problemas.",
    sections: [
      {
        h: "Por qué se llama así",
        p: [
          "Debe su nombre a Liz Power Suárez, coautora de XIMA: Zil es Liz al revés. Ella la aplicó en una partida de un torneo.",
        ],
      },
      {
        h: "La estructura",
        p: [
          "La maniobra une los tres primeros pisos: un bloque en la tercera planta, alineado y semicoordinado con un bloque del segundo piso, y un bloque escalera en el primer piso que permite al especial entrar por dos sitios distintos.",
        ],
        defs: [
          ["Coordinar", "Dos bloques alineados que pueden subirse uno sobre el otro."],
          ["Semicoordinar", "Dos bloques alineados en los que solo uno de ellos puede subirse sobre el otro."],
        ],
      },
      {
        h: "Las cuatro fases",
        list: [
          "1. Ubicar un bloque en la tercera planta. Puede ser cualquier casilla del tercer piso, pero mejor las exteriores, sobre todo las esquinas, porque además protegen la subida del especial.",
          "2. Alinear y semicoordinar un bloque en una casilla interior del segundo piso con el del tercero. El bloque del tercer piso puede subirse sobre el del segundo, así que nadie puede dominar este último sin arriesgarse a perder su bloque.",
          "3. Crear un bloque escalera en el primer piso que permita alinear y coordinar el bloque que entrará con más de una casilla del segundo piso. Normalmente la mejor casilla es la central interior del primer piso.",
          "4. Esquivar el bloqueo del rival e incorporar el especial en una casilla desde la que luego pueda dominar el bloque escalera del segundo piso.",
        ],
        after: ["Con la estructura montada, solo queda aplicar la maniobra de final adecuada para llegar a la cima."],
      },
    ],
    faqs: [
      ["¿En qué fase se usa la maniobra Zil?", "En el medio juego, para llevar el especial a la tercera planta."],
    ],
    related: ["estrategia/medio-juego", "estrategia/maniobra-feedback", "estrategia/finales-xima", "modos/freebox"],
    cta: { label: "Maniobra Feedback", slug: "estrategia/maniobra-feedback" },
  },
  {
    slug: "estrategia/maniobra-feedback",
    theme: "estrategia",
    nav: "Maniobra Feedback",
    title: "La maniobra Feedback de XIMA: subir primero que el rival",
    description:
      "La maniobra Feedback de XIMA en 5 fases: planificación, inversión, creación, lanzamiento y recuperación. Así subes un bloque de piso antes que el rival.",
    h1: "Maniobra Feedback",
    intro:
      "La Feedback es la maniobra que, según el libro, garantiza llevar un bloque a un piso superior antes que el contrario. Aparece en todas las fases, pero se usa sobre todo en la apertura.",
    sections: [
      {
        h: "La idea",
        p: [
          "Se basa en alinear tres bloques que no se pueden detener dominándolos desde abajo. Primero se invierte un tiempo en ganar altura y después esa altura protege la escalera y permite subir, y volver a capturar una y otra vez.",
        ],
      },
      {
        h: "Las cinco fases",
        list: [
          "1. Planificación: elegir dónde se va a montar la estructura. Hay que mirar antes las amenazas del rival: por ejemplo, si el rival tiene un bloque en M2, poner un bloque en B2 sería un error, porque lo dominaría por la fila 2.",
          "2. Inversión: invertir un tiempo en doblar columna, es decir, subir un bloque propio sobre otro (por ejemplo, en A1).",
          "3. Creación: ahora sí, crear el bloque escalera en B2. Si el rival lo domina, el bloque doblado de A1, que ya tiene altura suficiente, lo puede capturar.",
          "4. Lanzamiento: la fase más peligrosa. Se ataca la casilla interior del piso superior para tener una escalera doble y catapultar el bloque de A1 dos pisos de golpe, hasta E5.",
          "5. Recuperación: se recogen los frutos. Si el rival intenta frenar la subida sacrificando bloques en B2, el bloque de D4 vuelve a capturar una y otra vez. De ahí el nombre: la maniobra se retroalimenta hasta que el rival se queda sin bloques que sacrificar.",
        ],
      },
      {
        h: "Cómo practicarla",
        p: [
          "Monta la posición en FreeBox: dos bloques en A1 (uno encima del otro), uno en B2 y otro en D4, y comprueba cómo el bloque de arriba de A1 sube por la diagonal hasta E5. Después juega contra el bot en Fácil e intenta ejecutarla en tus primeras jugadas.",
        ],
      },
    ],
    faqs: [
      ["¿Para qué sirve la maniobra Feedback?", "Para llevar un bloque a un piso superior antes que el rival, protegiendo la escalera con un bloque doblado."],
    ],
    related: ["reglas/subir-bloques", "estrategia/aperturas-xima", "estrategia/maniobra-zil", "modos/freebox"],
    cta: { label: "Subir de piso", slug: "reglas/subir-bloques" },
  },
  {
    slug: "estrategia/finales-xima",
    theme: "estrategia",
    nav: "Finales",
    title: "Finales en XIMA: cómo llegar a la cima",
    description:
      "Cómo ganar los finales de XIMA: por qué dos bloques ganan a uno, la maniobra dos contra uno, la oposición de los especiales y el valor del tiempo.",
    h1: "Finales",
    intro:
      "El final es la fase en la que se decide quién llega a G7. Pocas piezas, mucha precisión: aquí la oposición de los especiales y el tiempo lo son todo.",
    sections: [
      {
        h: "Dos bloques ganan a uno",
        p: [
          "El final ideal consiste en llevar dos bloques propios con acceso a la tercera planta, uno de ellos el especial, para maniobrar con ellos y asegurar la subida a la cima. Lo más importante es entender que dos bloques siempre ganan a uno, aunque ese uno sea un especial. Si hay más bloques rivales en la tercera planta, el plan es neutralizarlos hasta llegar a un dos contra uno.",
        ],
      },
      {
        h: "La maniobra dos contra uno",
        list: [
          "1. Llevar y mantener el especial en una casilla central del anillo interior del tercer piso.",
          "2. Alinear y mantener alineado, desde las casillas más cercanas, el bloque común con el especial propio.",
          "3. Aprovechar la oposición de los especiales para encontrar el ángulo o punto ciego desde el que el común puede dar apoyo para subir a la cima sin que el especial rival lo domine.",
        ],
        after: [
          "La clave de la última fase es encontrar dos casillas que el rival no pueda defender a la vez. Mientras mantengas el especial en una central interior y el común alineado en una casilla donde no lo dominen, lo conseguirás.",
        ],
      },
      {
        h: "Conceptos generales de final",
        list: [
          "Si tu especial llega libre y solo a la tercera planta, colócalo en las líneas centrales o en las diagonales exteriores, alineado con G7.",
          "Si en el tercer piso hay un bloque contrario, coloca el especial en casillas oscuras: tienen más posibilidades de movimiento y apoyo.",
          "Si el especial contrario está dominado, dobla y sube tu especial a toda costa.",
          "Doblar bloques comunes propios en el anillo interior del tercer piso, en la diagonal o en la central interior, es la defensa más poderosa sin ayuda del especial.",
          "Desvía la ofensiva: amenaza al especial contrario desde una casilla que no esté alineada con G7 para ganar tiempo.",
        ],
      },
      {
        h: "El tiempo",
        p: [
          "En los finales el tiempo es decisivo: a menudo se sacrifica todo para llegar a la cima, y a veces llegar primero a la tercera planta decide la partida. Cuenta cuántos turnos necesita tu especial para llegar a G7 y cuántos el del rival antes de decidir.",
        ],
      },
      {
        h: "El especial cambia de papel",
        p: [
          "En la apertura y el medio juego el especial es un bloque defensivo. En el final se vuelve muy ofensivo, gracias a la oposición: es el único que puede cerrar el paso al especial rival.",
        ],
      },
    ],
    faqs: [
      ["¿Por qué dos bloques ganan a uno?", "Porque con dos bloques siempre se puede encontrar una casilla de apoyo para la subida que el especial rival no alcanza, usando la oposición."],
      ["¿Cómo practico los finales?", "Con los puzzles diarios y montando posiciones en FreeBox."],
    ],
    related: ["reglas/oposicion-especial", "reglas/cima", "puzzles", "reglas/amenazas-al-especial"],
    cta: { label: "Resolver puzzles", slug: "puzzles" },
    legacy: ["estrategia/tiempo-en-finales", "estrategia/final-dos-contra-uno"],
  },
  {
    slug: "reglas/amenazas-al-especial",
    theme: "estrategia",
    nav: "Amenazas al especial",
    title: "Cómo defender tu bloque especial en XIMA",
    description:
      "Qué hacer cuando amenazan tu bloque especial en XIMA: moverlo, dominar al atacante, bloquear, recapturar, contraatacar o amenazar la cima con un común.",
    h1: "Amenazas al especial",
    intro:
      "Tu especial está amenazado cuando un bloque rival puede terminar encima de él en su próximo movimiento. El libro describe todas las formas conocidas de evitarlo, de la mejor a la más desesperada.",
    sections: [
      {
        h: "Cómo reconocer una amenaza",
        p: [
          "Mira las ocho líneas que salen de tu especial (horizontales, verticales y diagonales). Si en alguna hay un bloque rival que puede llegar hasta él respetando la regla de la altura, tu especial está amenazado. Un mismo especial puede estar amenazado por varios bloques a la vez.",
        ],
      },
      {
        h: "Las formas de defenderse",
        list: [
          "Moverlo a una casilla que no esté atacada, en el mismo piso, más arriba o más abajo.",
          "Dominar con el especial un bloque propio o rival; si es el atacante, mejor todavía, e incluso puedes capturarlo.",
          "Proteger el especial dominando con otro bloque, de forma que si te lo dominan puedas recapturar.",
          "Bloquear u obstruir a los bloques atacantes. Ojo: tapar a uno no sirve si hay otro atacante por otra línea.",
          "Desviar o preparar una recaptura: colocar un bloque de forma que, si dominan tu especial, puedas capturar al bloque que lo hizo.",
          "Contraatacar al especial contrario para ganar tiempo.",
          "Como recurso desesperado, amenazar la cima con un común: si capturan tu especial, podrías rescatarlo enseguida y volver a meterlo en mejor posición.",
        ],
      },
      {
        h: "¿Siempre hay que mover el especial?",
        p: [
          "No. Muchas veces es mejor tapar la línea de ataque, recapturar o contraatacar, sobre todo si mover el especial lo aleja de su camino a la cima. Elige la defensa que mantenga una ruta hacia G7.",
        ],
      },
    ],
    faqs: [
      ["¿Qué pasa si dominan mi especial?", "Queda inmovilizado mientras siga tapado. Si además lo capturan, puedes rescatarlo llevando un común a la cima."],
    ],
    related: ["reglas/rescate", "reglas/oposicion-especial", "reglas/dominacion-y-captura", "estrategia/finales-xima"],
    cta: { label: "El rescate", slug: "reglas/rescate" },
  },
  {
    slug: "estrategia/valor-de-los-bloques",
    theme: "estrategia",
    nav: "Valor de los bloques",
    title: "El valor de los bloques en XIMA: quién va ganando",
    description:
      "Cuánto vale cada bloque en XIMA según el libro: valor por piso, por dominar y por quedar dominado, y cómo calcular en un momento quién va ganando la partida.",
    h1: "Valor de los bloques",
    intro:
      "En XIMA todo tiene valor: los bloques, las casillas y lo que cada bloque está haciendo. El libro propone una forma sencilla de poner números a una posición para saber quién va mejor.",
    sections: [
      {
        h: "Valor absoluto y valor relativo",
        p: [
          "Cada bloque tiene un valor absoluto, que depende del piso en el que está apoyado, y un valor relativo, que cambia según la escasez de material y las funciones que cumple (dominar, bloquear, servir de escalón…). Lo mismo pasa con el tablero: cada casilla vale distinto según su altura y su cercanía a la cima.",
          "Quien mejor entienda el valor de cada bloque en cada momento estará más cerca de la victoria.",
        ],
      },
      {
        h: "Los valores según el piso",
        table: {
          head: ["Dónde está", "Bloque común", "Bloque especial"],
          rows: [
            ["Fuera del tablero (al empezar)", "2", "2"],
            ["Primer piso", "2", "4"],
            ["Segundo piso", "4", "8"],
            ["Tercer piso", "6", "12"],
            ["Cima", "8", "16"],
          ],
        },
        after: [
          "Así se deducen de los ejemplos del libro: cada piso suma 2 puntos y el especial vale el doble por su responsabilidad de llegar a la cima. Por eso llegar a G7 con el especial aporta 16 puntos de fuerza de juego en el CIMAX.",
        ],
      },
      {
        h: "Dominar y ser dominado",
        p: [
          "Un bloque que domina suma la mitad del valor de los bloques que tiene debajo, porque desde ahí tiene más posibilidades. Y el bloque dominado se queda con la mitad de su valor: no desaparece del todo, porque todavía impide que el de arriba se vaya sin liberarlo.",
          "Ejemplo del libro: en el primer piso, un bloque que domina a otro pasa a valer 3 puntos (2 + 1) y el dominado, 1. En el tercer piso, el que domina vale 9 (6 + 3) y el dominado, 3. Un especial en la tercera planta que domina a dos bloques de 6 vale 12 + 3 + 3 = 18.",
          "También suma 1 punto por cada bloque rival que esté bloqueando.",
        ],
      },
      {
        h: "¿Quién va ganando?",
        p: [
          "La fuerza de una posición se calcula sumando el valor de tus bloques en el tablero y el de los que tienes fuera. Una vez metido el primer bloque, cada bloque de fuera vale lo mismo que el promedio de tus bloques en el tablero.",
          "Ejemplo del libro: los azules tienen 3 bloques en juego que suman 12 puntos (promedio 4) y 7 fuera: 12 + 7 × 4 = 40 puntos. Los rojos tienen 4 bloques que suman 12 (promedio 3) y 6 fuera: 12 + 6 × 3 = 30. Aunque en el tablero estén empatados, los azules van ganando, porque lograron lo mismo con un bloque menos.",
        ],
      },
      {
        h: "Úsalo con cabeza",
        p: [
          "El propio libro advierte que es casi imposible saber cuánto vale de verdad un bloque, porque sus funciones cambian a cada jugada, y que estos valores pueden cambiar en el futuro. Úsalos como guía para comparar dos jugadas cuando dudes, no como una ley.",
        ],
      },
    ],
    faqs: [
      ["¿Cuánto vale un bloque en XIMA?", "Según el libro, un común vale 2 puntos en el primer piso, 4 en el segundo y 6 en el tercero; el especial vale el doble."],
    ],
    related: ["estadisticas/cimax", "estrategia/medio-juego", "reglas/dominacion-y-captura"],
    cta: { label: "El CIMAX", slug: "estadisticas/cimax" },
    legacy: ["cimax/valor-tablero-bloques"],
  },
  {
    slug: "articulos/ciencia-y-azar-en-juegos",
    theme: "estrategia",
    nav: "Ciencia, azar y Cienzar",
    title: "Juegos de ciencia, de azar y Cienzar: el nuevo género",
    description:
      "¿Un juego de mesa puede ser a la vez de estrategia y de suerte? Qué son los juegos de ciencia, los de azar y el género Cienzar que propone XIMA.",
    h1: "Ciencia, azar y Cienzar",
    intro:
      "Casi todos los juegos de mesa caen en uno de dos grupos: los de pura estrategia, como el ajedrez, y los de suerte, como el parchís. XIMA propone un tercero.",
    sections: [
      {
        h: "Juegos de ciencia",
        p: [
          "Son juegos tácticos, estratégicos y lógicos, con igualdad de condiciones y sin suerte. Gana quien, observando la partida, está mejor preparado gracias al entrenamiento y al estudio. El ajedrez, las damas y el go son juegos de ciencia, y XIMA también lo es en su Modo Ciencia.",
        ],
      },
      {
        h: "Juegos de azar",
        p: [
          "No exigen una gran preparación: dependen de un factor suerte tan alto que puede cambiar el resultado por lógica que parezca la partida. Por eso son ideales para jugar en familia, con gente de distinto nivel. El Modo Azar de XIMA, con sus dos dados, pertenece a este grupo, aunque la estrategia sigue contando mucho.",
        ],
      },
      {
        h: "Cienzar: ciencia + azar",
        p: [
          "El libro «Cómo llegar a la Xima» define por primera vez un tercer género: el juego Cienzar, híbrido con y sin azar, de nivel táctico y estratégico muy alto, con igualdad de decisiones para todos y resultados prácticamente impredecibles.",
          "Su objetivo es que todos los jugadores tengan la oportunidad de ganar, por bueno que sea el rival, y que perder duela menos. En XIMA se juega de dos maneras: en Modo Cienzar cada jugador elige en su turno si usa los dados, y en Ciencia V/S Azar un bando juega sin dados y el otro con ellos.",
        ],
      },
      {
        h: "Por qué importa",
        p: [
          "Mezclar decisión y azar obliga a pensar de otra forma: no basta con calcular la mejor jugada, hay que decidir cuándo arriesgar. Según el libro, esto refleja muy bien muchas situaciones de la vida real, como administrar recursos o saber cuándo hace falta un sacrificio.",
        ],
      },
    ],
    faqs: [
      ["¿Qué significa Cienzar?", "Es la unión de ciencia y azar: un juego híbrido en el que se combinan las decisiones libres y los dados."],
    ],
    related: ["cienzar", "modos/ciencia-vs-azar", "modos-de-juego", "articulos/xima-y-el-ajedrez"],
    cta: { label: "Modo Cienzar", slug: "cienzar" },
  },
  {
    slug: "articulos/xima-y-el-ajedrez",
    theme: "estrategia",
    nav: "XIMA y el ajedrez",
    title: "XIMA y el ajedrez: parecidos y diferencias",
    description:
      "Si te gusta el ajedrez, te gustará XIMA. En qué se parecen (notación, aperturas, finales) y en qué se diferencia: tablero 3D, piezas iguales, dados y sin tablas.",
    h1: "XIMA y el ajedrez",
    intro:
      "XIMA nació pensando en el ajedrez, y quien juega ajedrez se siente en casa enseguida. Pero también tiene diferencias que cambian por completo la forma de pensar.",
    sections: [
      {
        h: "En qué se parecen",
        list: [
          "Son juegos de estrategia en los que, sin dados, gana el mejor preparado.",
          "Las partidas tienen apertura, medio juego y final.",
          "Se apuntan con una notación algebraica: las casillas tienen letra y número.",
          "Hay teoría de aperturas con nombre propio, como la Capablanca, dedicada al campeón cubano.",
          "Se puede medir el nivel de los jugadores con una puntuación (el CIMAX en XIMA).",
        ],
      },
      {
        h: "En qué se diferencian",
        table: {
          head: ["", "Ajedrez", "XIMA"],
          rows: [
            ["Tablero", "Plano, 8 × 8", "Pirámide de 4 pisos, 13 × 13"],
            ["Piezas", "6 tipos que se mueven distinto", "10 bloques que se mueven igual"],
            ["Objetivo", "Dar jaque mate al rey", "Llevar el bloque especial a la cima"],
            ["Empate", "Puede haber tablas", "Nunca: se añade material"],
            ["Azar", "No hay", "Opcional: hay modos con dados"],
            ["Jugadores", "2", "De 2 a 4"],
          ],
        },
      },
      {
        h: "La tercera dimensión",
        p: [
          "La gran diferencia es la altura. En XIMA no basta con controlar casillas: hay que construir escalones para subir de piso, y una pieza puede pasar por encima de otras siempre que la altura lo permita. Eso hace que cada posición tenga muchísimas posibilidades: según el libro, unos 1.700 millones de combinaciones posibles ya en el segundo movimiento.",
        ],
      },
      {
        h: "Más fácil de aprender",
        p: [
          "Como todas las piezas se mueven igual, las reglas de XIMA se aprenden en pocos minutos. Lo difícil, como en el ajedrez, es jugar bien.",
        ],
      },
    ],
    faqs: [
      ["¿XIMA se parece al ajedrez?", "Sí en que es un juego de estrategia con notación, aperturas y finales; no en que su tablero es una pirámide 3D, todas las piezas se mueven igual y nunca hay tablas."],
      ["¿Si sé jugar al ajedrez me costará menos aprender XIMA?", "Sí. Muchas ideas, como el desarrollo, la iniciativa o el estudio de los finales, son muy parecidas."],
    ],
    related: ["que-es-xima", "historia-de-xima", "estrategia", "sistema-de-anotacion"],
    cta: { label: "Aprende a jugar", slug: "como-jugar" },
  },
];
